import { format, isAfter, startOfMonth, sub } from "date-fns";
import { enGB } from "date-fns/locale";
import { CalendarIcon, ChevronDown } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Calendar } from "@/components/ui/calendar";
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
	Popover,
	PopoverContent,
	PopoverTrigger,
} from "@/components/ui/popover";
import { useConsumption } from "@/context/ConsumptionContext";
import type { TimeframeOption } from "@/types";
import { MonthPicker } from "../ui/monthpicker";
import { YearPicker } from "../ui/year-picker";

const formatMap: Record<TimeframeOption, string> = {
	Daily: "dd/MM/yyyy",
	Weekly: "dd/MM/yyyy",
	Monthly: "MMM yyyy",
	Yearly: "yyyy",
};

const labelMap: Record<TimeframeOption, string> = {
	Daily: "Jour",
	Weekly: "Semaine",
	Monthly: "Mois",
	Yearly: "Année",
};

export const DateTimeSelector = () => {
	const today = new Date();
	const dayBefore = sub(today, { days: 2 });
	const {
		setTimeframe,
		setSelectedDate,
		timeframe = "Monthly",
		selectedDate = today.getDate() <= 5
			? startOfMonth(sub(today, { months: 1 }))
			: startOfMonth(today),
	} = useConsumption();

	return (
		<div className="flex items-center gap-2 w-full max-w-md">
			{/* Date Picker */}
			<Popover>
				<PopoverTrigger asChild>
					<Button
						variant="outline"
						className={`flex-1 min-w-[150px] sm:w-auto justify-between text-left font-normal px-3 py-2 rounded-md border border-gray-300 ${
							!selectedDate ? "text-gray-500" : ""
						}`}
					>
						{selectedDate
							? format(selectedDate, formatMap[timeframe])
							: "Select date"}
						<CalendarIcon className="ml-2 h-5 w-5 text-gray-600" />
					</Button>
				</PopoverTrigger>
				<PopoverContent className="w-full sm:w-auto p-2 shadow-lg border rounded-md bg-white">
					{["Daily", "Weekly"].includes(timeframe) ? (
						<Calendar
							mode="single"
							selected={selectedDate}
							onSelect={(newDate) => newDate && setSelectedDate(newDate)}
							weekStartsOn={1}
							locale={enGB}
							initialFocus
							disabled={(date) => isAfter(date, dayBefore)}
						/>
					) : timeframe === "Monthly" ? (
						<MonthPicker
							selectedMonth={selectedDate}
							onMonthSelect={(newDate) => setSelectedDate(newDate ?? null)}
							maxDate={dayBefore}
						/>
					) : (
						<YearPicker
							year={selectedDate.getFullYear()}
							onYearChange={(newYear) =>
								setSelectedDate(
									new Date(
										newYear,
										selectedDate.getMonth(),
										selectedDate.getDate(),
									),
								)
							}
							maxYear={dayBefore.getFullYear()}
						/>
					)}
				</PopoverContent>
			</Popover>

			{/* Timeframe Dropdown */}
			<DropdownMenu>
				<DropdownMenuTrigger asChild>
					<Button
						variant="outline"
						className="flex items-center justify-between w-30 px-3 py-2 rounded-md border border-gray-300"
					>
						{labelMap[timeframe]}
						<ChevronDown className="ml-2 h-5 w-5 text-gray-600" />
					</Button>
				</DropdownMenuTrigger>
				<DropdownMenuContent className="w-30 bg-white border rounded-md shadow-lg">
					<DropdownMenuItem onClick={() => setTimeframe("Daily")}>
						Jour
					</DropdownMenuItem>
					<DropdownMenuItem onClick={() => setTimeframe("Weekly")}>
						Semaine
					</DropdownMenuItem>
					<DropdownMenuItem onClick={() => setTimeframe("Monthly")}>
						Mois
					</DropdownMenuItem>
					<DropdownMenuItem onClick={() => setTimeframe("Yearly")}>
						Année
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenu>
		</div>
	);
};

export default DateTimeSelector;
